import * as React from "react"
import {
  Relay, indexDocs, listKinds, listProjects, createDoc, readDoc, getBoard, moveItem,
  promoteIdea, failed,
  type BoardView, type Doc, type DocIndex, type Kind, type Project,
} from "./lib/client"
import { logout as apiLogout, entitled, accessToken, restore } from "./lib/faion"
import {
  categories, categoryLabel, categoryParent, describe, inLanguage, languageName,
  rememberLanes, FALLBACK_LANES, type Category, type DocEntry, type Lane,
} from "./lib/docs"
import {
  boards as allBoards, childrenOf, describeDir, laneNodes, ownDir, prettify,
} from "./lib/tree"
import { parse, go, replace, type Route, type Where } from "./lib/route"
import { restore as restoreTheme } from "./lib/theme"
import { Board } from "./components/Board"
import { Cards } from "./components/Cards"
import { Home } from "./components/Home"
import { Reader } from "./components/Reader"
import { Sidebar } from "./components/Sidebar"
import { SignIn } from "./components/SignIn"

/**
 * The product surface.
 *
 * Two independent axes, and keeping them independent is the architecture rather than a
 * convenience: **who you are** comes from faion.net, **what you can read** comes from the
 * client on the reader's own machine. Signing in fetches no content, and content is not
 * ours to withhold (`DEC-2`) — somebody whose subscription lapses still has their
 * documents, because they were never here.
 *
 * **The address bar holds where you are.** Every page has a URL, so the back button walks
 * the way you came, a reload lands where you were, and a link to a board can be sent to
 * somebody. Nothing here navigates by setting state — it sets the hash, and the hash sets
 * the state. A component that did both would be a page back cannot return from.
 *
 * **One client, several projects.** The project is a segment of the address, because a
 * shared link that meant different things to two people would be worse than no link.
 */
const App: React.FC = () => {
  const [auth, setAuth] = React.useState<{ allowed: boolean; reason: string } | null>(null)
  const [ready, setReady] = React.useState(false)
  const [relay, setRelay] = React.useState<Relay | null>(null)
  const [clientOnline, setClientOnline] = React.useState<boolean | null>(null)

  const [route, setRoute] = React.useState<Route>(() => parse(location.hash))
  const { project, where, view } = route

  const [projects, setProjects] = React.useState<Project[]>([])
  const [entries, setEntries] = React.useState<DocEntry[]>([])
  const [lanes, setLanes] = React.useState<Lane[]>(FALLBACK_LANES)
  const [cats, setCats] = React.useState<Category[]>([])
  const [kinds, setKinds] = React.useState<Kind[]>([])
  const [languages, setLanguages] = React.useState<string[]>([])
  // The reading language is a preference, not a place, so it stays out of the address and
  // in storage: a link should carry which document, not which language somebody reads in.
  const [lang, setLang] = React.useState<string>(
    () => localStorage.getItem("faion.lang") ?? "")
  const [repo, setRepo] = React.useState("")

  const [open, setOpen] = React.useState<Doc | null>(null)
  const [board, setBoard] = React.useState<BoardView | null>(null)
  const [busy, setBusy] = React.useState<string | null>(null)
  const [err, setErr] = React.useState("")
  const [drawer, setDrawer] = React.useState(false)
  // Whether the menu is showing is a property of this screen, so it is remembered here and
  // not in the address: a link should open where it points, at whatever width you are at.
  const [collapsed, setCollapsed] = React.useState(
    () => localStorage.getItem("faion.menu") === "off")

  React.useEffect(() => { restoreTheme() }, [])

  const collapse = (off: boolean) => {
    localStorage.setItem("faion.menu", off ? "off" : "on")
    setCollapsed(off)
  }

  // -- the address is the state ------------------------------------------

  React.useEffect(() => {
    const read = () => setRoute(parse(location.hash))
    window.addEventListener("hashchange", read)
    return () => window.removeEventListener("hashchange", read)
  }, [])

  const nav = React.useCallback((next: Where, v = "") => {
    setErr(""); setDrawer(false)
    go({ project, where: next, view: v })
  }, [project])

  // -- session -----------------------------------------------------------

  const refresh = React.useCallback(() => entitled().then(setAuth), [])

  React.useEffect(() => { restore().finally(() => refresh().finally(() => setReady(true))) },
    [refresh])

  React.useEffect(() => {
    const token = accessToken()
    if (!auth?.allowed || !token) {
      relay?.close(); setRelay(null); setClientOnline(null)
      setEntries([]); setCats([]); setOpen(null); setBoard(null)
      return
    }
    // `live` is not ceremony. A closing socket reports presence on its way out, so a
    // connection this effect has already replaced would otherwise write `offline` over the
    // state of the one that succeeded it — and the surface says "no client is connected"
    // about a client sitting right there.
    let live = true
    const r = new Relay(token)
    r.onPresence = on => { if (live) setClientOnline(on) }
    r.connect().then(() => { if (live) setRelay(r) }).catch(e => { if (live) setErr(String(e)) })
    return () => { live = false; r.close() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.allowed])

  // -- the index ---------------------------------------------------------

  const load = React.useCallback((r: Relay, id: string) => {
    return indexDocs(r, id || undefined).then((d: DocIndex) => {
      const langs = d.languages?.length ? d.languages : ["en"]
      // A stored preference for a language the repository no longer keeps is stale, not a
      // request: fall back rather than showing every document as untranslated.
      setLanguages(langs)
      setLang(prev => (langs.includes(prev) ? prev : langs[0]))
      const ls = d.lanes?.length ? d.lanes : FALLBACK_LANES
      rememberLanes(ls)
      setLanes(ls)
      setEntries(d.documents)
      setCats(categories(d.documents, ls))
      setRepo(d.repo)
    })
  }, [])

  React.useEffect(() => {
    if (!relay || !clientOnline) return
    let live = true
    listProjects(relay).then(({ projects: ps }) => {
      if (!live) return
      setProjects(ps)
      // An address naming a project this client does not serve is stale, not a request.
      // Correcting it in place rather than pushing a step keeps the back button honest.
      if (!ps.some(p => p.id === project) && ps.length) {
        const fixed = { project: ps[0].id, where, view }
        replace(fixed)
        setRoute(fixed)
      }
    }).catch(e => { if (live) setErr(String(e)) })
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relay, clientOnline])

  React.useEffect(() => {
    if (!relay || !clientOnline || !project) return
    load(relay, project).catch(e => setErr(String(e)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relay, clientOnline, project])

  // Where a new thing goes is wherever you are standing.
  const parent = where.at === "dir" ? where.path
    : where.at === "category" ? categoryParent(where.id)
    : "docs"

  React.useEffect(() => {
    if (!relay || !clientOnline) return
    // What may be created depends on the place: inside a board, that board's items; outside
    // one, documents and boards. A client too old to answer simply offers nothing.
    listKinds(relay, project || undefined, parent)
      .then(k => setKinds(k.kinds ?? []))
      .catch(() => setKinds([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relay, clientOnline, project, parent])

  // -- what the current address needs fetched ----------------------------

  const boardIndexAt = React.useCallback((dir: string): DocEntry | undefined =>
    entries.find(e => ownDir(e) === dir && e.meta?.board), [entries])

  const loadBoard = React.useCallback((dir: string) => {
    if (!relay) return
    const index = boardIndexAt(dir)
    if (!index) { setBoard(null); return }
    getBoard(relay, index.path, project || undefined)
      .then(v => setBoard(failed(v) ? null : v))
      .catch(e => setErr(String(e)))
  }, [relay, project, boardIndexAt])

  React.useEffect(() => {
    if (where.at === "dir") loadBoard(where.path)
    else setBoard(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [where, entries])

  React.useEffect(() => {
    if (!relay || where.at !== "doc") { setOpen(null); return }
    let live = true
    setBusy(where.path)
    readDoc(relay, where.path, project || undefined)
      .then(d => { if (live) setOpen(d) })
      .catch(e => { if (live) setErr(String(e)) })
      .finally(() => { if (live) setBusy(null) })
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relay, project, where])

  // -- acting ------------------------------------------------------------

  const chooseLang = (next: string) => {
    localStorage.setItem("faion.lang", next)
    setLang(next)
    // Stay on the document, change its telling. Switching language should never be a way
    // of losing your place.
    const current = entries.find(e => e.path === open?.path
      || Object.values(e.variants ?? {}).some(v => v.path === open?.path))
    if (current) nav({ at: "doc", path: inLanguage(current, next).path })
  }

  const reload = async () => { if (relay) await load(relay, project) }

  const create = async (kind: string, name: string, itemParent: string | null) => {
    if (!relay) return
    const res = await createDoc(relay, {
      project: project || undefined, kind, name,
      lane: where.at === "category" && where.id !== "unfiled" ? where.id : null,
      parent, item_parent: itemParent,
    })
    // A refusal comes back as a value, not a rejection — the client answers every op in
    // its payload — so it has to be checked rather than awaited away.
    if (failed(res)) throw new Error(res.detail || res.error)
    if (res.ok === false) throw new Error("the repository's own checks refused it")
    await reload()
    if (where.at === "dir") loadBoard(where.path)
  }

  const move = async (path: string, state: string) => {
    if (!relay) return
    const res = await moveItem(relay, path, state, project || undefined)
    if (failed(res)) throw new Error(res.detail || res.error)
    if (res.ok === false) throw new Error("the repository's own checks refused it")
    if (where.at === "dir") loadBoard(where.path)
  }

  const promote = async (path: string, target: string, kind: string) => {
    if (!relay) return
    const res = await promoteIdea(relay, path, target, kind, project || undefined)
    if (failed(res)) throw new Error(res.detail || res.error)
    if (res.ok === false) throw new Error("the repository's own checks refused it")
    await reload()
    if (where.at === "dir") loadBoard(where.path)
  }

  if (!ready) return <div className="sf-gate"><p className="sf-quiet">One moment…</p></div>
  if (!auth?.allowed) return <SignIn onDone={refresh} reason={auth?.reason} />

  const current = where.at === "category" ? where.id
    : where.at === "dir" ? where.path : ""
  const summaryFor = (id: string) => laneSummary(id, entries, lanes)
  const declared = new Set(lanes.map(l => l.id))

  return (
    <div className={`sf-app ${cats.length && !collapsed ? "has-sidebar" : ""}`}>
      {!!cats.length && (
        <>
          <button className="sf-menu-btn"
                  onClick={() => (collapsed ? collapse(false) : setDrawer(v => !v))}
                  aria-label="Categories">≡</button>
          <button className={`sf-backdrop ${drawer ? "show" : ""}`} tabIndex={-1}
                  aria-hidden="true" onClick={() => setDrawer(false)} />
          <div className={`sf-sidebar ${drawer ? "open" : ""} ${collapsed ? "is-off" : ""}`}>
            <Sidebar repo={repo} projects={projects} project={project} categories={cats}
                     entries={entries} languages={languages} lang={lang} current={current}
                     clientOnline={clientOnline} who={auth.reason} lanes={lanes}
                     onCollapse={() => collapse(true)}
                     onProject={id => go({ project: id, where: { at: "home" }, view: "" })}
                     onCategory={id => nav({ at: "category", id })}
                     onEnter={path => nav({ at: "dir", path })}
                     onOpen={path => nav({ at: "doc", path })}
                     onLang={chooseLang}
                     onHome={() => nav({ at: "home" })}
                     onSignOut={() => apiLogout().finally(refresh)} />
          </div>
        </>
      )}

      <main className="sf-main">
        {err && <p className="sf-note bad">{err}</p>}

        {clientOnline === false && (
          <div className="sf-note warn">
            <b>No client is connected.</b>
            The client holds the agent, your AI credentials and your working copy — none of
            which we have, and none of which we can start for you. Run it on the machine
            that has your repository: it dials out to us, so it needs no open port and works
            from anywhere.
          </div>
        )}

        {clientOnline === null && <p className="sf-quiet">Connecting to your client…</p>}

        {clientOnline && where.at === "doc" && open && (() => {
          const entry = entries.find(e => e.path === open.path
            || Object.values(e.variants ?? {}).some(v => v.path === open.path))
            ?? { path: open.path, title: open.path, lane: null, kind: "document", folder: "" }
          const telling = inLanguage(entry, lang)
          const dir = entry.folder || "docs"
          return (
            <>
              <button className="sf-ghost sf-back"
                      onClick={() => nav(dir === "docs"
                        ? { at: "home" } : { at: "dir", path: dir })}>
                ← {dir === "docs" ? "Home" : prettify(dir.split("/").pop() ?? dir)}
              </button>
              <Reader doc={open} meta={describe(entry, lang)} lane={entry.lane}
                      loading={busy === open.path}
                      untranslated={!telling.translated && languages.length > 1}
                      language={languageName(lang)}
                      original={languageName(languages[0] ?? "en")} />
            </>
          )
        })()}

        {clientOnline && where.at === "dir" && board && (() => {
          const d = describeDir(entries, where.path)
          return (
            <Board view={board} title={d.title} summary={d.summary} kinds={kinds}
                   boards={allBoards(entries)} parents={holders(entries)}
                   titleOf={p => entries.find(e => e.path === p)?.title ?? p}
                   mode={view} onMode={m => nav(where, m)}
                   onOpen={path => nav({ at: "doc", path })}
                   onEnter={path => nav({ at: "dir", path })}
                   onMove={move} onPromote={promote} onCreate={create} />
          )
        })()}

        {clientOnline && where.at === "dir" && !board && (() => {
          const d = describeDir(entries, where.path)
          return (
            <Cards title={d.title} summary={d.summary} kicker="section" path={where.path}
                   nodes={childrenOf(entries, where.path)} indexEntry={d.entry}
                   lang={lang} kinds={kinds}
                   onOpen={path => nav({ at: "doc", path })}
                   onEnter={path => nav({ at: "dir", path })}
                   onCreate={create} />
          )
        })()}

        {clientOnline && where.at === "category" && (
          <Cards title={categoryLabel(where.id)} kicker="category" path=""
                 summary={summaryFor(where.id)}
                 nodes={laneNodes(entries, where.id, declared)}
                 lang={lang} kinds={kinds}
                 onOpen={path => nav({ at: "doc", path })}
                 onEnter={path => nav({ at: "dir", path })}
                 onCreate={create} />
        )}

        {clientOnline && where.at === "home" && (
          <Home title={projects.find(p => p.id === project)?.name ?? "Your documents"}
                repo={repo} categories={cats} lanes={lanes} entries={entries} lang={lang}
                declared={declared}
                laneSummary={summaryFor}
                onOpen={path => nav({ at: "doc", path })}
                onEnter={path => nav({ at: "dir", path })}
                onCategory={id => nav({ at: "category", id })} />
        )}
      </main>
    </div>
  )
}

/**
 * What a new item could be part of — **from every board in the project**.
 *
 * A technical story answers to a feature on the *product* board, which is the whole reason
 * the technical board holds stories at all. A picker limited to the board being looked at
 * could not express the one link the boards exist to carry.
 *
 * Only epics and features are offered: a story is a slice, and a slice of a slice is a
 * hierarchy nobody asked for.
 */
function holders(entries: DocEntry[]): Array<{
  path: string; title: string; kind: string; where: string
}> {
  const boardTitle = (p: string) => {
    const dir = p.split("/").slice(0, -2).join("/")
    return entries.find(e => ownDir(e) === dir)?.title ?? prettify(dir.split("/").pop() ?? "")
  }
  return entries
    .filter(e => e.meta?.item === "epic" || e.meta?.item === "feature")
    .map(e => ({ path: e.path, title: e.title, kind: e.meta!.item, where: boardTitle(e.path) }))
    .sort((a, b) => a.where.localeCompare(b.where) || a.title.localeCompare(b.title))
}

/** A lane's own description, when the repository wrote one, else what the lane is for. */
function laneSummary(id: string, entries: DocEntry[], lanes: Lane[]): string {
  const index = entries.find(e => ownDir(e) === `docs/${id}`)
  if (index?.summary) return index.summary
  return LANE_NOTE[id] ?? `Everything filed as ${
    lanes.find(l => l.id === id)?.label.toLowerCase() ?? id}.`
}

/** What each lane is for — the question it answers, which is the test for whether a
 * document belongs in it. Shown only until the repository writes its own `index.md`. */
const LANE_NOTE: Record<string, string> = {
  product: "What we are building and for whom.",
  tech: "How it is built.",
  marketing: "How people hear about it.",
  economics: "Whether the numbers add up.",
  ops: "How we work — process, gates, registries.",
  legal: "What we are obliged to.",
  unfiled: "In the repository and in no section. Give each one a lane and it moves.",
}

export default App
